/**
 * Soft-palette color picker for chat agents.
 * Provides muted, pleasant colors suitable for card backgrounds.
 */

import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/** Soft color palette — muted tones that work as card accent colors. */
export const AGENT_COLORS = [
  { name: 'Slate', value: '#94a3b8', bg: '#f1f5f9' },
  { name: 'Stone', value: '#a8a29e', bg: '#f5f5f4' },
  { name: 'Rose', value: '#f9a8d4', bg: '#fff1f2' },
  { name: 'Pink', value: '#f0abfc', bg: '#fdf2f8' },
  { name: 'Violet', value: '#c4b5fd', bg: '#f5f3ff' },
  { name: 'Indigo', value: '#a5b4fc', bg: '#eef2ff' },
  { name: 'Blue', value: '#93c5fd', bg: '#eff6ff' },
  { name: 'Sky', value: '#7dd3fc', bg: '#f0f9ff' },
  { name: 'Cyan', value: '#67e8f9', bg: '#ecfeff' },
  { name: 'Teal', value: '#5eead4', bg: '#f0fdfa' },
  { name: 'Emerald', value: '#6ee7b7', bg: '#ecfdf5' },
  { name: 'Green', value: '#86efac', bg: '#f0fdf4' },
  { name: 'Lime', value: '#bef264', bg: '#f7fee7' },
  { name: 'Yellow', value: '#fde047', bg: '#fefce8' },
  { name: 'Amber', value: '#fcd34d', bg: '#fffbeb' },
  { name: 'Orange', value: '#fdba74', bg: '#fff7ed' },
];

/** Look up the background tint for a given agent color value. Falls back to muted gray. */
export function getAgentColorBg(colorValue: string | undefined | null): string {
  if (!colorValue) return ''; // no color → use default bg-muted via className
  const entry = AGENT_COLORS.find((c) => c.value === colorValue);
  return entry?.bg ?? '#f8fafc';
}

interface AgentColorPickerProps {
  value?: string;
  onChange?: (color: string) => void;
  disabled?: boolean;
}

export function AgentColorPicker({
  value,
  onChange,
  disabled = false,
}: AgentColorPickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const current = AGENT_COLORS.find((c) => c.value === value);

  const handleSelect = (color: string) => {
    onChange?.(color);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          disabled={disabled}
          className='h-10 gap-2 px-3'
          type='button'
        >
          <div
            className={`h-5 w-5 rounded-full border ${value ? 'border-border/50' : 'border-border bg-muted'}`}
            style={value ? { backgroundColor: value } : undefined}
          />
          <span className='text-sm text-muted-foreground'>
            {current?.name || 'None'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-64 p-3' align='start'>
        <h4 className='text-sm font-medium mb-2'>Choose a color</h4>
        <div className='grid grid-cols-8 gap-1.5'>
          {/* None / reset option */}
          <button
            type='button'
            onClick={() => handleSelect('')}
            className={cn(
              'h-7 w-7 rounded-full border bg-muted transition-transform hover:scale-110',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              !value
                ? 'ring-2 ring-ring ring-offset-1 border-transparent'
                : 'border-border/40',
            )}
            title='None'
          />
          {AGENT_COLORS.map((color) => (
            <button
              key={color.value}
              type='button'
              onClick={() => handleSelect(color.value)}
              className={cn(
                'h-7 w-7 rounded-full border transition-transform hover:scale-110',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                value === color.value
                  ? 'ring-2 ring-ring ring-offset-1 border-transparent'
                  : 'border-border/40',
              )}
              style={{ backgroundColor: color.value }}
              title={color.name}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
